import { useCallback, useEffect, useState } from "react";

export const useDialInteraction = (
  dialRef: React.RefObject<HTMLDivElement>,
  setSelectedMonth: (months: number) => void
) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDialInteraction = useCallback(
    (clientX: number, clientY: number) => {
      if (! dialRef.current) return;

      const rect = dialRef. current.getBoundingClientRect();
      const centerX = rect.left + rect. width / 2;
      const centerY = rect.top + rect.height / 2;

      const deltaX = clientX - centerX;
      const deltaY = clientY - centerY;

      let angle = Math.atan2(deltaX, -deltaY) * (180 / Math. PI);
      if (angle < 0) angle += 360;

      const months = Math.max(
        1,
        Math. min(12, Math.round((angle / 360) * 12) || 12)
      );
      setSelectedMonth(months);
    },
    [dialRef, setSelectedMonth]
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    handleDialInteraction(e.clientX, e.clientY);
  };

  const handleMouseMove = useCallback(
    (e:  MouseEvent) => {
      if (isDragging) {
        handleDialInteraction(e.clientX, e.clientY);
      }
    },
    [isDragging, handleDialInteraction]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches[0];
    handleDialInteraction(touch.clientX, touch.clientY);
  };

  const handleTouchMove = useCallback(
    (e:  TouchEvent) => {
      if (isDragging) {
        const touch = e.touches[0];
        handleDialInteraction(touch.clientX, touch.clientY);
      }
    },
    [isDragging, handleDialInteraction]
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  return {
    isDragging,
    handleMouseDown,
    handleTouchStart,
  };
};